import React from 'react';
import { Modal, message } from 'antd';
import ResumeSection from '../components/ResumeSection';
import ListSectionView from '../com/ListSectionView';
import JobSelectModal from '../components/JobSelectModal';
import { occupationSection } from '../configs/sectionConfig';
import { buildMonthRangeParams } from '../utils/resumeTransforms';
import Config from '../../../config/Config';
import WidgetAjax from '../../../widget/WidgetAjax';

/**
 * 判断单个表单字段值是否"为空" — 拷贝自 hooks/useResumeSubmit.js 的 isEmptyValue,
 * 在本模块本地复刻避免引入 hooks 文件副作用。行为完全一致:
 *   - undefined / null / '' → true
 *   - [] → true
 *   - dayjs 但 invalid() → true
 *   - 空对象 {} → true
 *   - 其它 → false
 *
 * (注: 原 useResumeSubmit.js 是 React Hook, 静态 import 非 hook 命名导出在
 *  ESM 下不会触发 hook 上下文, 但放在 hooks/ 目录里语义上不清晰, 故本地复刻。)
 */
const isEmptyValue = (value) => {
  if (value === undefined || value === null || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (value && typeof value === 'object') {
    if (value.format && value.isValid && !value.isValid()) return true;
    return Object.keys(value).length === 0;
  }
  return false;
};

/**
 * 判断整条记录是否全部字段都为空 — 拷贝自 hooks/useResumeSubmit.js 的 isEmptyRecord。
 * 命中时短路: 关闭编辑态, 不发请求 (与原 saveListSection workHistory 分支等价 + 不发空表单)。
 */
const isEmptyRecord = (record, fields = []) => {
  if (!record || typeof record !== 'object') return true;
  return fields.every((field) => isEmptyValue(record[field.props]));
};

/**
 * 工作/实习经历 模块 (class 组件版)
 *
 * - props:
 *   - resumeState:    由父页面传入的 resumeData, 含 raw / info;
 *                     本模块自行读 raw.occupations (与 EducationAbilityModule 对齐)
 *   - options:        由 useResumeOptions 注入, 含 jobTypeList / 字典
 *   - applicantId:    当前用户 id, 仅删除接口携带; 工作经历 add/update 后端通过
 *                     access token 识别申请人, 本模块不再重复传 applicant_id
 *   - onChanged:      任意持久化操作 (add/update/remove) 成功后回调, 由父页面
 *                     触发 reloadResume; 本模块不维护 items / selectedJobs 等本地草稿
 *   - reloadJobTypes: (word) => void, 由 useResumeOptions 注入, 给 JobSelectModal
 *                     "搜索岗位"输入框; 未传时退化为 no-op
 *
 * - state:
 *   - editingIndex:        正在编辑 list 里的哪一条 (undefined = 不在编辑态)
 *   - adding:              是否在"添加新条"态 (与 editingIndex 互斥)
 *   - jobTypeModalVisible: 岗位类型选择器是否可见
 *   - jobTypeSelectedItems:岗位类型选择器的已选项数组 (selectedItems 走这里)
 *
 * - 编辑/添加/删除 全部走本模块方法, 直接调 WidgetAjax:
 *   - 新增 → /applicant/add_occupation      (不带 id, 不带 applicant_id)
 *   - 编辑 → /applicant/update_occupation   (带 id, 不带 applicant_id)
 *   - 删除 → /applicant/remove_occupation   (带 record.id + applicant_id)
 *
 *   入参转换复用 buildMonthRangeParams, OCCDATE → OCCFROM + OCCTO;
 *   与原 useResumeSubmit.saveListSection('workHistory', ...) **等价 + 不发空表单**:
 *     - 入参构造完全一致 (add/update 不重复 applicant_id)
 *     - 顶部新增 isEmptyRecord(values, editableFields) 短路:
 *       全部可编辑字段为空时 closeEditor 不发请求 (拷贝自原 hook 的 isEmptyRecord)
 *
 * - 岗位类型选择器:
 *   - 使用 class JobSelectModal (single + thirdLevelOnly)
 *   - 确认时把 code 写到 formRef.current 的 JOBTYPE, name 写到 JOBTYPE_CN
 *   - **不信任** onTrigger 的第二参数作为"记录下标", 改用 this.state.editingIndex
 *
 * - 不引用任何 hooks / useResumeEditor / useResumeSubmit, 父页面只需:
 *     <WorkExperienceModule
 *       resumeState={resumeData}
 *       options={options}
 *       applicantId={applicantId}
 *       reloadJobTypes={reloadJobTypes}
 *       onChanged={reloadResume}
 *     />
 *
 * - 展示/编辑容器复用 ResumeSection + ListSectionView, 保持 resume-new 样式,
 *   OCCDATE customDate 渲染由 ResumeEditorForm 内部处理。
 */
class WorkExperienceModule extends React.Component {
  formRef = React.createRef();

  constructor(props) {
    super(props);
    this.state = {
      editingIndex: undefined,
      adding: false,
      jobTypeModalVisible: false,
      jobTypeSelectedItems: [],
    };
  }

  // ============ 编辑态 ============

  startEdit = (idx) => {
    this.setState({ editingIndex: idx, adding: false });
  };

  startAdd = () => {
    this.setState({ editingIndex: undefined, adding: true });
  };

  closeEditor = () => {
    this.setState({ editingIndex: undefined, adding: false });
  };

  // ============ 删除 ============

  /**
   * 删除某条工作经历
   * - record 必须有 id (server 已落库条目)
   * - 走 Modal.confirm 二次确认, 取消即返回, 不调接口
   */
  handleDelete = (record) => {
    if (!record || !record.id) {
      message.error('记录缺少 id, 无法删除');
      return;
    }

    Modal.confirm({
      title: '确认删除',
      content: '确定删除这条工作/实习经历吗？',
      okText: '确认',
      cancelText: '取消',
      onOk: () => this.handleRemoveRemote(record),
    });
  };

  handleRemoveRemote = (record) => {
    const { applicantId, onChanged } = this.props;
    WidgetAjax.ajax({
      url: `${Config.getBasePath()}/applicant/remove_occupation`,
      params: { id: record.id, applicant_id: applicantId },
      callback: (res) => {
        if (res && res.status === 0) {
          message.success('删除成功');
          if (typeof onChanged === 'function') onChanged();
          return;
        }
        message.error((res && res.message) || '删除失败');
      },
    });
  };

  // ============ 表单提交 ============

  /**
   * ListSectionView → ResumeEditorForm → onFinish(values) 回调入口
   * - record: 当前正在编辑的记录 (undefined = 新增, 否则为已有条目)
   * - 字段定义完全复用 occupationSection.formList, 这里只负责:
   *   1) isEmptyRecord 短路: 所有可编辑字段都为空 → closeEditor 不发请求
   *      (与原 useResumeSubmit.saveListSection workHistory 分支完全等价 + 不发空表单)
   *   2) buildMonthRangeParams 把 OCCDATE → OCCFROM + OCCTO
   *   3) 按 add / update 分流调接口, 参数构造**严格对齐**原 saveListSection:
   *      update 带 record.id, add 不带 id 也不带 applicant_id
   *      (workHistory 后端通过 access token 识别申请人, 无须重复传 applicant_id)
   *   4) 成功后关编辑态 + 通知父组件刷新
   */
  handleSubmit = (values, record) => {
    const { onChanged } = this.props;

    // 1) 空表单短路 — 与原 useResumeSubmit.saveListSection 顶部 isEmptyRecord 分支等价
    const editableFields = (occupationSection.formList || [])
      .filter((field) => field.type !== 'tips' && field.props);
    if (isEmptyRecord(values, editableFields)) {
      this.closeEditor();
      return;
    }

    // 2) 入参构造: 严格对齐 useResumeSubmit.saveListSection workHistory 分支
    //    - update: { id: record.id, ...values, OCCFROM, OCCTO }
    //    - add:    { ...values, OCCFROM, OCCTO }   (不带 applicant_id, 也不带 id)
    const params = {
      ...values,
      ...buildMonthRangeParams(values, 'OCCDATE', 'OCCFROM', 'OCCTO'),
    };

    const isUpdate = record && record.id;
    const url = isUpdate
      ? `${Config.getBasePath()}/applicant/update_occupation`
      : `${Config.getBasePath()}/applicant/add_occupation`;
    const finalParams = isUpdate ? { id: record.id, ...params } : { ...params };

    const successText = isUpdate ? '保存成功' : '添加成功';
    const errorText = isUpdate ? '保存失败' : '添加失败';

    WidgetAjax.ajax({
      url,
      params: finalParams,
      callback: (res) => {
        if (res && res.status === 0) {
          message.success(successText);
          this.closeEditor();
          if (typeof onChanged === 'function') onChanged();
          return;
        }
        // 失败提示 + 保留编辑态 (不调 closeEditor)
        message.error((res && res.message) || errorText);
      },
    });
  };

  // ============ 字段触发 → 打开岗位类型选择器 ============

  /**
   * ListSectionView.onTrigger(field, fieldIndex) 路由:
   *   - occupationJobType → 打开 JobSelectModal (single + thirdLevelOnly)
   *   其它 trigger 在本模块作用域外, 不处理
   *
   * 注意: 这里**不信任** onTrigger 的第二个参数作为"记录下标"使用 —
   * ResumeEditorForm.renderField 调用 onTrigger(field, index) 时传的 index
   * 是字段在 formList 里的下标,不是 list 条目下标。
   * 当前正在编辑的条目下标应一律取自 this.state.editingIndex。
   */
  handleFieldTrigger = (field) => {
    if (field && field.trigger === 'occupationJobType') {
      this.openJobTypeModal();
    }
  };

  openJobTypeModal = () => {
    // 编辑态: 按 this.state.editingIndex 取当前记录, 把 JOBTYPE/JOBTYPE_CN
    // 作为 initial 选中, 这样用户重选时也能看到自己已选中的项高亮。
    // adding 态下 editingIndex 为 undefined, items 为空, Modal 打开即为空选。
    const { resumeState = {} } = this.props;
    const list = (resumeState.raw && resumeState.raw.occupations) || [];
    const { editingIndex } = this.state;
    const record = typeof editingIndex === 'number' ? list[editingIndex] : undefined;
    const items = [];
    if (record && record.JOBTYPE_CN) {
      items.push({
        code: record.JOBTYPE,
        name: record.JOBTYPE_CN,
      });
    }

    this.setState({
      jobTypeModalVisible: true,
      jobTypeSelectedItems: items,
    });
  };

  closeJobTypeModal = () => {
    this.setState({
      jobTypeModalVisible: false,
      jobTypeSelectedItems: [],
    });
  };

  /**
   * JobSelectModal.onConfirm(items) → 把 code / name 写到 formRef 当前表单
   * - items 是 [{code, name, ...}], single 模式只取第一个
   * - JOBTYPE  存 code (后端入参)
   * - JOBTYPE_CN 存 name (前端展示)
   */
  handleJobTypeConfirm = (items) => {
    const item = (items && items[0]) || null;
    if (item && this.formRef.current) {
      this.formRef.current.setFieldValue('JOBTYPE', item.code);
      this.formRef.current.setFieldValue('JOBTYPE_CN', item.name);
    }
    this.closeJobTypeModal();
  };

  // ============ ListSectionView 所需 callback ============

  // certificate / works 是其它 section 的 state, 工作经历不涉及, 传 no-op 避免破坏 ResumeEditorForm 字段.
  setCertificateFile = () => {};
  setWorksFile = () => {};

  // ============ 渲染 ============

  render() {
    const { resumeState = {}, options, applicantId, reloadJobTypes } = this.props;
    const {
      editingIndex,
      adding,
      jobTypeModalVisible,
      jobTypeSelectedItems,
    } = this.state;
    const { jobTypeList = [] } = options || {};

    // 主页面已经把 /applicant/resume_info 的原始数据放在 resumeData.raw,
    // 本模块直接读 raw.occupations, 不再做归一化.
    const list = (resumeState.raw && resumeState.raw.occupations) || [];

    return (
      <>
        <ResumeSection
          section={occupationSection}
          editing={typeof editingIndex === 'number'}
          adding={adding}
        >
          <ListSectionView
            section={occupationSection}
            list={list}
            applicantId={applicantId}
            editingIndex={editingIndex}
            adding={adding}
            options={options}
            formRef={this.formRef}
            setCertificateFile={this.setCertificateFile}
            setWorksFile={this.setWorksFile}
            onTrigger={this.handleFieldTrigger}
            onSubmit={(values) => this.handleSubmit(values, list[editingIndex])}
            onCancel={this.closeEditor}
            onEdit={this.startEdit}
            onDelete={this.handleDelete}
            onAdd={this.startAdd}
          />
        </ResumeSection>

        {/* 岗位类型选择器 (工作/实习经历): single + thirdLevelOnly
            - 与原 ResumeNewPage jobTypeModal 行为一致:
              selectedCodes / selectedItems 直接从 state 传,
              确认时回写 formRef.JOBTYPE/JOBTYPE_CN。 */}
        <JobSelectModal
          single
          thirdLevelOnly
          visible={jobTypeModalVisible}
          jobTypeList={jobTypeList}
          selectedCodes={jobTypeSelectedItems.map((it) => it.code).filter(Boolean)}
          selectedItems={jobTypeSelectedItems}
          onSearch={reloadJobTypes}
          onCancel={this.closeJobTypeModal}
          onConfirm={this.handleJobTypeConfirm}
        />
      </>
    );
  }
}

export default WorkExperienceModule;